import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { SocketRPC, socketEmit } from '../lib/socket';
import useProducerLocations from '../store/producer-locations';
import useUser from '../store/user';

import _ from 'lodash';

import { DailyReportFile, DailyReportFolder, DailyReportFolderGroup, DateBounds } from '../../types';
import DailyReportsTable from '../components/DailyReportsTable';

const DailyReportsPage = () => {
	const { user } = useUser();
	const { producerKey } = user || {};
	const [dailyReportFolderGroups, setDailyReportFolderGroups] = useState<DailyReportFolderGroup>({});
	const [selectedDailyReportFolderGroup, setSelectedDailyReportFolderGroup] = useState<DailyReportFolder[] | null>(
		null
	);
	const [dailyReportFiles, setDailyReportFiles] = useState<DailyReportFile[]>([]);
	const [dailyReportsLoaded, setDailyProducerReportsLoaded] = useState<boolean>(true);
	const { producerLocations } = useProducerLocations();
	const [activePage, setActivePage] = useState<number>(1);
	const [nextPageToken, setNextPageToken] = useState(null); // GDrive gives us a token to get the next page of results; doesn't work for prev pages

	const [locationQuery, setLocationQuery] = useState<string>();
	const debouncedSetLocationQuery = _.debounce(setLocationQuery, 500);

	const [dateBounds, setDateBounds] = useState<DateBounds>({
		startDate: null,
		endDate: null,
	});

	const getDownloadUrl = (file: DailyReportFile) => {
		const { fileId, fileName, parentFolderId, parentFolderName } = file;
		return socketEmit(SocketRPC.GetDailyReportDownloadUrl, {
			fileId,
			parentFolderId,
			fileName,
			parentFolderName,
			user,
		}).then((res) => {
			if (!res.success) {
				throw new Error(res.error);
			}
			if (!res.url) {
				throw new Error('No download url found');
			}
			return res.url;
		});
	};

	useEffect(() => {
		setDailyProducerReportsLoaded(false);
		if (!producerLocations) {
			return;
		}
		Promise.resolve()
			.then(() => {
				return socketEmit(SocketRPC.GetDailyReportsFolderForProducer, {
					producerKey,
					user,
				});
			})
			.then((res) => {
				if (!res.success) {
					setDailyReportFolderGroups({});
					throw new Error(res.error);
				}

				const folders = _.chain(res.folders)
					.map((folder: any) => {
						const { folderId, folderName } = folder;

						const producerLocation = producerLocations.find(
							(pl: any) => pl.producerLocationKey === folderName
						);
						const producerLocationKeyName = producerLocation?.producerLocationName || folderName;

						return {
							folderId,
							folderName,
							producerLocationKeyName,
						};
					})
					.groupBy((folder: any) => folder.producerLocationKeyName) // we have some duplicate folders in Gdrive so need to consolidate
					.sort((a: any, b: any) => a.producerLocationKeyName.localeCompare(b.producerLocationKeyName))
					.value();

				setDailyReportFolderGroups(folders);
				setDailyProducerReportsLoaded(true);
			})
			.catch((err) => {
				toast.error(`Error getting daily reports. Please try again`);
				setDailyProducerReportsLoaded(true);
				console.log(`Could not get daily reports`, err);
			});
	}, [user, producerLocations]);

	const searchDailyReportFiles = (pageToken = null) => {
		const { startDate, endDate } = dateBounds;

		const modifiedStartDate = startDate // add 1 days to the start date; reports are generated 1 day later
			? new Date(new Date(startDate).getTime() + 1 * 24 * 60 * 60 * 1000)
			: null;

		const modifiedEndDate = endDate // add 2 days to the end date; reports are generated 1 day later and upper bound should be EOD
			? new Date(new Date(endDate).getTime() + 2 * 24 * 60 * 60 * 1000)
			: null;

		setDailyProducerReportsLoaded(false);
		Promise.resolve()
			.then(() => {
				return socketEmit(SocketRPC.GetDailyReportsByProducerLocationKeyFolders, {
					selectedFolders: selectedDailyReportFolderGroup,
					startDate: modifiedStartDate,
					endDate: modifiedEndDate,
					pageToken,
					pageSize: 10,
					user,
				});
			})
			.then((res) => {
				if (!res.success) {
					setDailyReportFiles([]);
					throw new Error(res.error);
				}
				setDailyReportFiles(res.files);
				setNextPageToken(res.nextPageToken);
				setDailyProducerReportsLoaded(true);
			})
			.catch((err) => {
				toast.error(`Error getting daily reports. Please try again`);
				setDailyProducerReportsLoaded(true);
				console.log(`Could not get daily reports`, err);
			});
	};

	useEffect(() => {
		setActivePage(1);
		setNextPageToken(null);

		if (!selectedDailyReportFolderGroup) {
			setDailyReportFiles([]);
			setLocationQuery(undefined);
			return;
		}
		searchDailyReportFiles();
	}, [selectedDailyReportFolderGroup, dateBounds]);

	const paginationProps = {
		activePage,
		setActivePage,
		onNext: () => searchDailyReportFiles(nextPageToken),
	};

	const DailyReportsTableProps = {
		dailyReportFolderGroups,
		selectedDailyReportFolderGroup,
		setSelectedDailyReportFolderGroup,
		dailyReportFiles,
		dailyReportsLoaded,
		setDailyReportFiles,
		getDownloadUrl,
		locationQuery,
		debouncedSetLocationQuery,
		dateBounds,
		setDateBounds,
		paginationProps: nextPageToken ? paginationProps : null,
	};

	return DailyReportsTable(DailyReportsTableProps);
};

export default DailyReportsPage;
